// Zustand Store for NAZ AI Diagnosis State
import { create } from 'zustand';
import { DiagnosisSchema, type DiagnosisData } from '../schemas/diagnosisSchema';

interface NazStore {
    diagnosis: DiagnosisData | null;
    updateFromAiResponse: (response: any) => void;
    resetDiagnosis: () => void;
    clearSession: () => void;
}

export const useNazStore = create<NazStore>((set) => ({
    diagnosis: null,

    // Robust update function using Zod schema validation
    updateFromAiResponse: (response: any) => {
        try {
            const result = DiagnosisSchema.safeParse(response);
            if (result.success) {
                set({ diagnosis: result.data });
            } else {
                console.warn("AI Response validation failed, using fallbacks:", result.error.format());
                // Create a basic fallback that matches the schema structure
                const fallback: DiagnosisData = {
                    Is_FollowUp: response?.Is_FollowUp ?? false,
                    FollowUp_Question: response?.FollowUp_Question ?? null,
                    risk_level: response?.risk_level ?? "low",
                    customerReport: response?.customerReport || null,
                    technicalGuide: response?.technicalGuide || null,
                    hotspots: response?.hotspots || []
                };
                set({ diagnosis: fallback });
            }
        } catch (error) {
            console.error("Critical error updating diagnosis from AI response:", error);
            // Absolute fallback
            set({ diagnosis: null });
        }
    },

    resetDiagnosis: () => set({ diagnosis: null }),

    clearSession: () => set({ diagnosis: null })
}));

// Helper function to get risk color
export const getRiskColor = (riskLevel: "low" | "medium" | "high"): string => {
    const colors = {
        low: "bg-green-100 border-green-500 text-green-800",
        medium: "bg-yellow-100 border-yellow-500 text-yellow-800",
        high: "bg-red-100 border-red-500 text-red-800"
    };
    return colors[riskLevel] || colors.low;
};

// Helper function to get risk icon (Legacy - will be replaced by Lucide React components)
export const getRiskIcon = (riskLevel: "low" | "medium" | "high"): string => {
    const icons = {
        low: "✅",
        medium: "⚠️",
        high: "🚨"
    };
    return icons[riskLevel] || icons.low;
};
